/**
 * Display formatting.
 *
 * The `numeric` helpers exist for one reason: drizzle's `avg()` and `sum()`
 * come back as STRINGS. Rendering `row.averagePlanogramScore` directly puts
 * "87.33333333333333" on the screen, and it type-checks, because the field
 * genuinely is a string. Every aggregate goes through here.
 *
 * Visit durations are NOT here. They are `formatDuration` in `@fieldvision/
 * contracts`, because the api prints the same value into the PDF and the two
 * must not drift again — they did, and the console said "1d" where the export
 * said "0m" for one visit. `age` below is the deliberate exception.
 */

import { formatDuration } from '@fieldvision/contracts';

const ID = 'id-ID';

/** A numeric-string aggregate, rounded. Returns the em-dash for null. */
export function decimal(value: string | number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return parsed.toLocaleString(ID, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * A price index: `1.2000` → `1,20×`.
 *
 * The `×` is what stops it being read as a percentage or a price — this is a
 * multiple of OUR observed price, not a currency and not a score out of 100.
 *
 * FIXED decimals, unlike `decimal`'s caller-chosen default and unlike the two
 * page-local copies this replaces. Target and actual sit adjacent in
 * right-aligned `tabular-nums` columns, and a ragged `1,2×` over `1,25×` defeats
 * the digit alignment those columns exist for.
 */
export function multiplier(value: string | number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return `${parsed.toLocaleString(ID, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}×`;
}

/** A score out of 100. */
export function percent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return `${Math.round(parsed)}%`;
}

export function integer(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return Math.round(parsed).toLocaleString(ID);
}

/**
 * A large count, shortened for a KPI tile.
 *
 * `1.234.567` set in Sora at 2rem is about 150px wide, and a `grid-cols-2` tile
 * on a 375px screen is about 163px — so the exact number either overflows or
 * forces a font size that no longer reads as the headline figure. Below 100,000
 * nothing is abbreviated, because that is the range these numbers actually live
 * in and "8,2 rb" is harder to read than "8.234".
 *
 * The exact value belongs in the tile's hint and `title` attribute; this is for
 * the glanceable figure only, never for a table cell.
 */
export function compact(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  if (Math.abs(parsed) < 100_000) return Math.round(parsed).toLocaleString(ID);
  if (Math.abs(parsed) < 1_000_000) return `${decimal(parsed / 1_000, 0)} rb`;
  return `${decimal(parsed / 1_000_000, 1)} jt`;
}

export function rupiah(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return new Intl.NumberFormat(ID, {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(parsed);
}

/* -------------------------------------------------------------------------- */
/* Input masking                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The digits a user actually typed, with everything else dropped.
 *
 * The inverse of `groupDigits`, and the value the form holds. Storing the masked
 * STRING in form state and parsing it on submit would mean `Number("12.500")` —
 * which in `id-ID` is twelve and a half thousand and in JavaScript is NaN. State
 * holds digits; the separator exists only on screen.
 */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * "12500" → "12.500", using the id-ID thousands separator.
 *
 * Grouping matters more here than it looks. A reference price is read back by
 * someone checking it against a price list, and 1500000 versus 150000 is a digit
 * nobody miscounts once the dots are there and everybody miscounts before.
 */
export function groupDigits(digits: string): string {
  if (digits === '') return '';
  const parsed = Number(digits);
  if (!Number.isFinite(parsed)) return '';
  return new Intl.NumberFormat(ID).format(parsed);
}

/**
 * Seconds → "3 dtk", "5 mnt", "2 jam 5 mnt". How OLD something is, not how long
 * it took — the worker heartbeat and the head of a queue.
 *
 * NOT `formatDuration`, and the difference is exactly one branch. A visit's
 * sub-minute precision is noise; an infrastructure age's IS the signal.
 * `heartbeatTone` turns amber at 90 seconds, so a healthy worker spends its whole
 * life inside the band `formatDuration` collapses to "<1 mnt" — an operator
 * watching liveness would see one unchanging string whether the last write was 2
 * seconds ago or 59, which is the only moving part on that panel. "<1 mnt lalu"
 * is not a sentence either.
 *
 * "dtk" and not "d", for the reason `formatDuration` spells its units out at
 * length: a bare "3d" is the ambiguity this pair exists to remove, and an infra
 * panel is not exempt from it. Above a minute this hands off, so the two cannot
 * disagree about the range they share.
 */
export function age(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (!Number.isFinite(seconds)) return '—';
  const total = Math.round(seconds);
  // A clock that moved backwards makes a heartbeat "-2 dtk" old, which reads as
  // a bug in this panel rather than in the thing it is watching.
  if (total < 60) return `${Math.max(0, total)} dtk`;
  return formatDuration(total);
}

/**
 * `age`, for a source that counts in milliseconds.
 *
 * IT EXISTS TO STOP ONE BUG, and only one — the same one the helper it replaces
 * existed for. `age(ms / 1000)` looks like the obvious call and is wrong for the
 * null case: `null / 1000` is `0` in JavaScript, so a queue with nothing waiting
 * would render "0 dtk" — a confident zero where the truth is "there is no oldest
 * job", which is the distinction `QueueHealth` documents `oldestWaitingMs` as
 * carrying. Guarding before the divide is the whole of the difference.
 */
export function ageMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (!Number.isFinite(ms)) return '—';
  return age(ms / 1000);
}

export function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(ID, { dateStyle: 'medium', timeStyle: 'short' });
}

export function dateOnly(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(ID, { dateStyle: 'medium' });
}

/** `YYYY-MM-DD` for a date input, in LOCAL time — `toISOString` would shift it. */
export function toDateInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * A `YYYY-MM-DD` from a date input, widened to the full day and converted to the
 * ISO instant the API expects.
 *
 * `endOfDay` matters: a report "to 6 August" that sends `2026-08-06T00:00:00Z`
 * silently excludes everything that happened on the sixth, which reads as
 * missing data rather than as an off-by-one.
 */
export function startOfDayIso(dateInput: string): string {
  return new Date(`${dateInput}T00:00:00`).toISOString();
}

export function endOfDayIso(dateInput: string): string {
  return new Date(`${dateInput}T23:59:59.999`).toISOString();
}

export function daysBetween(fromInput: string, toInput: string): number {
  const from = new Date(`${fromInput}T00:00:00`).getTime();
  const to = new Date(`${toInput}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

/**
 * A region in a picker — `900024 - BANDUNG`.
 *
 * RE-EXPORTED, NOT DEFINED HERE. It moved to `@fieldvision/contracts` when the
 * staff app started rendering the same string: that package is the one both
 * clients (and the api, which composes the same label in SQL) already depend on.
 * The re-export keeps this module the console's single formatting import rather
 * than making six call sites reach across for one function. See the definition
 * for why the code leads and why the picker sort has to follow.
 */
export { regionLabel } from '@fieldvision/contracts';
